import { Icon } from '@iconify/react'
import { observable, observe } from '@legendapp/state'
import { Computed, useObservable } from '@legendapp/state/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useLoaderData,
} from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { toast } from 'react-toastify'

import { deleteLicense, fetchLicenseContents, fetchLicenseList } from '~features/licenses/api'
import { Licenses } from '~features/licenses/types'
import {
  Button,
  Card,
  CardContent,
  DialogComponent,
  DropdownMenuComponent,
} from '~in-app/components'
import { Option } from '~in-app/components/MultiSelectDropdown'
import {
  FilterDropdownProps,
  TableContextProvider,
  TableState$,
} from '~in-app/components/table/TableContext'
import { auth$ } from '~in-app/legend-state'
import { fetchDistributorList, fetchSiteList } from '~in-app/shareable/index.api'
import { ListingResponse } from '~in-app/types/api.dto'

export const Route = createFileRoute('/console/sites/licenses/list')({
  component: LicensePage,
  loader: async () => {
    const privilegeValue = auth$.privileges.get()

    // 🔹 Recursive fetch helper
    async function fetchAllPages<T>(
      fetchFn: (params?: any) => Promise<ListingResponse<T> | null>,
    ): Promise<T[]> {
      let allItems: T[] = []
      let currentPage = 0
      let totalPages = 1

      while (currentPage < totalPages) {
        const response = await fetchFn({
          query: { page: currentPage },
        })

        if (!response) break

        allItems.push(...response.list)
        totalPages = response.totalPage ?? 1
        currentPage = (response.number ?? 0) + 1
      }

      return allItems
    }

    // 🔹 Fetch all distributors if user has privilege
    let clientIds: Option[] = []
    let siteIds: Option[] = []

    if (privilegeValue?.has('0.3.7.0')) {
      const allDistributors = await fetchAllPages(fetchDistributorList)
      clientIds = allDistributors.map((item: any) => ({
        value: item.id,
        label: item.name,
      }))
    }

    // 🔹 Fetch all sites
    if (privilegeValue.has('0.3.1.0')) {
      const allSites = await fetchAllPages(fetchSiteList)
      siteIds = allSites.map((item: any) => ({
        value: item.id,
        label: item.name,
      }))
    }

    return { clientIds, siteIds, privilegeValue }
  },
  staticData: {
    privileges: ['0.3.1.5'],
  },
})

const columnHelper = createColumnHelper<Licenses>()
const defaultState$ = observable<{
  sortList: { id: string, desc: boolean }[]
  pagination: number
  pageSize: number
  queries: string
  siteIds: string[] | undefined
  clientIds: string[] | undefined
  isChange: boolean
  isExpired: string | undefined
  isOpenDeleteLicense: boolean
  deleteLicenseId: string | undefined
  deleteLicenseName: string | undefined
}>({
  sortList: [{ id: 'name', desc: true }],
  pagination: 0,
  pageSize: 20,
  queries: '',
  siteIds: undefined,
  clientIds: undefined,
  isChange: false,
  isExpired: undefined,
  isOpenDeleteLicense: false,
  deleteLicenseId: undefined,
  deleteLicenseName: undefined,
})

function LicensePage() {
  const { clientIds, siteIds, privilegeValue } = useLoaderData({
    from: '/console/sites/licenses/list',
  })
  const navigate = Route.useNavigate()
  const statusDropDown: Option[] = [
    {
      label: 'Not Expired',
      value: 'not_expired',
    },
    {
      label: 'Expired',
      value: 'expired',
    },
  ]
  const dropdownItems = (value: Licenses) => {
    return [
      {
        label: 'Edit',
        onClick: () => navigate({ to: `/console/sites/licenses/edit/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.1.9'),
        icon: (
          <Icon
            height="16"
            icon="lucide:pencil"
            width="16"
          />
        ),
      },

      {
        label: 'Download',
        onClick: () => handleDownloadLicense(value.id, value.certNum),
        disabled: !privilegeValue?.has('0.3.1.7'),
        icon: (
          <Icon
            height="16"
            icon="lucide:download"
            width="16"
          />
        ),
      },
      {
        label: 'Duplicate',
        onClick: () => navigate({ to: `/console/sites/licenses/duplicate/${value.id}` }),
        disabled: !privilegeValue?.has('0.3.1.10'),
        icon: (
          <Icon
            height="16"
            icon="mdi:content-duplicate"
            width="16"
          />
        ),
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteLicense(value),
        disabled: !privilegeValue?.has('0.3.1.10'),
        icon: (
          <Icon
            height="16"
            icon="lucide:trash-2"
            width="16"
          />
        ),
      },
    ]
  }

  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor('site.name', {
      cell: info => info.getValue(),
      header: () => <span>Site Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('product.name', {
      cell: info => info.getValue(),
      header: () => <span>Product</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('site.systemId', {
      cell: info => info.getValue(),
      header: () => <span>System ID</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('certNum', {
      cell: info => info.getValue(),
      header: () => <span>Cert No.</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('current', {
      cell: info => (info.getValue() ? 'Active' : 'Inactive'),
      header: () => <span>Status</span>,
      enableSorting: true,
      size: 150,
    }),
    columnHelper.accessor('remarks', {
      cell: info => info.getValue(),
      header: () => <span>Remarks</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('activationExpiryTime', {
      cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'),
      header: () => <span>Activation Expiry Time</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('licenseExpiryDate', {
      cell: info => dayjs(info.getValue()).format('YYYY-MM-DD'),
      header: () => <span>License Expiry Date</span>,
      enableSorting: true,
    }),
    columnHelper.accessor('action', {
      cell: info => (
        <span>
          <DropdownMenuComponent
            preventClose
            buttonIcon={<Icon icon="lucide:ellipsis-vertical" />}
            className="rounded-md bg-transparent p-2 hover:opacity-100"
            items={dropdownItems(info.row.original)}
          />
        </span>
      ),
      header: () => <span />,
      enableSorting: false,
      size: 50,
    }),
  ]
  const state$ = useObservable(defaultState$)
  useEffect(() => {
    state$.pagination.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('page')
        return match ? parseInt(match, 10) - 1 : 0
      })(),
    )
    state$.pageSize.set(
      (() => {
        const match = new URLSearchParams(window.location.search).get('pageSize')
        return match ? parseInt(match, 10) : 20
      })(),
    )
    // const filteredClientIds = clientIds.filter(client => new URLSearchParams(window.location.search).get('clientIds')?.split(',').includes(client.value))
    // const filteredSiteIds = siteIds.filter(site => new URLSearchParams(window.location.search).get('siteIds')?.split(',').includes(site.value))
    const filteredQueries = new URLSearchParams(window.location.search).get('queries') || ''
    const filteredIsExpired = new URLSearchParams(window.location.search).get('isExpired') || undefined
    state$.clientIds.set(new URLSearchParams(window.location.search).get('clientIds')?.split(','))
    state$.siteIds.set(new URLSearchParams(window.location.search).get('siteIds')?.split(','))
    state$.queries.set(filteredQueries)
    state$.isExpired.set(filteredIsExpired)
    const disposer = observe(state$, (e) => {
      if (e.previous !== undefined && e.value !== undefined) {
        refetch()
      }
    }, { immediate: false })

    return disposer
  }, [])
  const filterDropdownItems: FilterDropdownProps[] = [
    {
      label: 'Search', // New dropdown for controlling sidebar
      type: 'text',
      value: new URLSearchParams(window.location.search).get('queries') || '',
      result: new URLSearchParams(window.location.search).get('queries') || '',
    },
    {
      id: 'siteIds',
      label: 'Sites',
      type: 'dropdown',
      isMultiSelect: true,
      options: siteIds,
      value: siteIds.filter(site => new URLSearchParams(window.location.search).get('siteIds')?.split(',').includes(site.value)),
      result: siteIds.filter(site => new URLSearchParams(window.location.search).get('siteIds')?.split(',').includes(site.value)),

    },
    {
      id: 'clientIds',
      label: 'Clients',
      type: 'dropdown',
      isMultiSelect: true,
      options: clientIds,
      value: clientIds.filter(client => new URLSearchParams(window.location.search).get('clientIds')?.split(',').includes(client.value)),
      result: clientIds.filter(client => new URLSearchParams(window.location.search).get('clientIds')?.split(',').includes(client.value)),

    },
    {
      id: 'isExpired',
      label: 'Is Expired',
      type: 'dropdown',
      isMultiSelect: false,
      options: statusDropDown,
      value: new URLSearchParams(window.location.search).get('isExpired') || undefined,
      result: new URLSearchParams(window.location.search).get('isExpired') || undefined,
    },
  ]
  const tableState$ = useObservable<TableState$<Licenses>>(() => ({
    id: 'licenses-table',
    isSortable: true,
    data: [],
    columns: columns,
    hideColumns: ['id'],
    sort: state$.sortList.get(),
    totalItem: 0,
    totalPage: 0,
    pagination: state$.pagination.get(),
    pageSize: state$.pageSize.get(),
    pageIndex: 0,
    filters: filterDropdownItems,
    onApply: (value: any) => {
      const searchParams: Record<string, any> = {}

      const textFilter = value.find((item: any) => item.label === 'Search')
      const textValue = textFilter?.result || ''
      state$.queries.set(textValue)
      searchParams.queries = textValue

      const sitesFilter = value.find((item: Option) => item.label === 'Sites')
      const sitesValue = sitesFilter?.result || []
      if (sitesValue.length > 0) {
        state$.siteIds.set(sitesValue.map((item: { value: any }) => item.value))
        searchParams.siteIds = sitesValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.siteIds.set(undefined)
        searchParams.siteIds = undefined
      }

      const clientFilter = value.find(
        (item: Option) => item.label === 'Clients',
      )
      const clientValue = clientFilter?.result || []
      if (clientValue.length > 0) {
        state$.clientIds.set(
          clientValue.map((item: { value: any }) => item.value),
        )
        searchParams.clientIds = clientValue.map((item: { value: any }) => item.value).join(',')
      }
      else {
        state$.clientIds.set(undefined)
        searchParams.clientIds = undefined
      }

      const isExpiredFilter = value.find(
        (item: Option) => item.label === 'Is Expired',
      )
      const isExpiredValue
        = isExpiredFilter?.result === 'not_expired'
          ? false
          : isExpiredFilter?.result === 'expired'
            ? true
            : undefined

      searchParams.isExpired = isExpiredFilter?.result

      state$.isExpired.set(isExpiredValue as any)

      state$.isChange.set(!state$.isChange.get())

      // Navigate with updated params
      navigate({
        search: (prev) => {
          const updatedSearch: Record<string, any> = { ...prev, ...searchParams } // Explicitly type as Record<string, any>

          // Remove empty or undefined keys safely
          if (!textFilter?.result) delete updatedSearch.queries
          if (!(sitesValue.length > 0)) delete updatedSearch.siteIds
          if (!(clientValue.length > 0)) delete updatedSearch.clientIds
          if (isExpiredFilter?.result === undefined) delete updatedSearch.isExpired

          return updatedSearch
        },
      })
    },
    setSort: (value: any) => {
      state$.sortList.set(value)
    },
    setPageSize: (value: any) => {
      state$.pageSize.set(value)
      navigate({ search: prev => ({ ...prev, pageSize: value }) })
    },
    setPagination: (value: any) => {
      state$.pagination.set(value)
      navigate({ search: prev => ({ ...prev, page: value + 1 }) })
    },
  }))

  const useDownloadLicense = () => {
    const mutation = useMutation({
      mutationFn: fetchLicenseContents,
      onSuccess: () => {
        toast.success('License downloaded successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const downloadMutation = useDownloadLicense()

  const handleDownloadLicense = (id: string, certNum: string) => {
    downloadMutation.mutate({ id, certNum })
  }

  const useDeleteLicense = () => {
    const mutation = useMutation({
      mutationFn: deleteLicense,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['license-listing'] })
        toast.success('License deleted successfully', { position: 'top-center', hideProgressBar: false, autoClose: 1000 })
      },
      onError: (error: Error) => {
        console.error('Error occurred:', error)
      },
    })

    return mutation
  }
  const deleteMutation = useDeleteLicense()

  const handleDeleteLicense = (value: Licenses) => {
    state$.deleteLicenseId.set(value.id)
    state$.deleteLicenseName.set(value.site.name)
    state$.isOpenDeleteLicense.set(true)
  }

  const queryClient = useQueryClient()

  const { refetch } = useQuery({
    queryKey: ['license-listing'],
    queryFn: async ({ signal }) => {
      const response = await fetchLicenseList({
        // abortCtrl: signal,
        query: {
          page: state$.pagination.get() || 0,
          size: state$.pageSize.get() || 20,
          queries: state$.queries.get() || undefined,
          isExpired: state$.isExpired.get(),
          clientIds: state$?.clientIds?.get()
            ? (state$?.clientIds?.get() ?? []).join(',')
            : undefined,
          siteIds: state$?.siteIds?.get()
            ? (state$?.siteIds?.get() ?? []).join(',')
            : undefined,
          sort: state$.sortList
            .get()
            .map((sortByVal) => {
              return `${sortByVal.id},${sortByVal.desc ? 'desc' : 'asc'}`
            })
            .join(','),
        },
      })
      const newLicenseList = response?.list.map((item: any) => ({
        ...item,
        action: '',
      })) as Licenses[]
      tableState$.data.set(newLicenseList || [])
      tableState$.totalItem.set(response?.totalItem ?? 0)
      tableState$.totalPage.set(response?.totalPage ?? 0)
      if ((response?.totalPage ?? 0) < (state$.pagination.get() + 1)) {
        state$.pagination.set(0)
        navigate({ search: prev => ({ ...prev, page: 1 }) })
      }
      return response
    },
  })
  return (
    <div>
      <div className="flex items-center justify-between p-2">
        <span className="mb-4 ml-4 text-2xl font-bold"> License Page</span>

        <Computed>
          {
            () => (
              <DialogComponent
                isOpen={state$.isOpenDeleteLicense.get()}
                submitButtonLabel="Delete"
                title={`Are You Sure To Delete license for '${state$.deleteLicenseName.get()}' site?`}
                onClose={() => { state$.isOpenDeleteLicense.set(false), state$.deleteLicenseId.set(undefined), state$.deleteLicenseName.set(undefined) }}
                onSubmit={
                  () => {
                    const deleteLicenseId = state$.deleteLicenseId.get()
                    if (deleteLicenseId) {
                      deleteMutation.mutate(deleteLicenseId as string)
                      state$.deleteLicenseId.set(undefined)
                      state$.deleteLicenseName.set(undefined)
                      state$.isOpenDeleteLicense.set(false)
                    }
                  }
                }
              />
            )
          }
        </Computed>

        <Button
          className="rounded-md border border-slate-400 bg-transparent font-medium text-muted-foreground hover:opacity-100"
          color="muted"
          disabled={!privilegeValue.has('0.3.1.6')}
          onClick={() => navigate({ to: '/console/sites/licenses/add' })}
        >
          Add
        </Button>
      </div>

      <div className="p-2">
        <Card className="mt-4">
          <CardContent>
            <div>
              <TableContextProvider state$={tableState$} />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
